import { isArrayLike, observable, computed } from 'mobx';
import { CultureSearchTransform } from '../models/configuration/CultureSearchTransform';
import cultureSearchTransformReplace from '../utils/cultureSearchTransformReplace';
import { SearchEntity } from '@kurtosys/ksys-api-client/dist/requests/fund/SearchEntity';
import { SearchDocuments } from '@kurtosys/ksys-api-client/dist/requests/documents/SearchDocuments';
import { ListDisclaimers } from '@kurtosys/ksys-api-client/dist/requests/config/ListDisclaimers';
import { ListFundLists } from '@kurtosys/ksys-api-client/dist/requests/config/ListFundLists';
import { ListTranslations } from '@kurtosys/ksys-api-client/dist/requests/config/ListTranslations';
import { RetrieveDocument } from '@kurtosys/ksys-api-client/dist/requests/documents/RetrieveDocument';
import { RetrieveDocumentZip } from '@kurtosys/ksys-api-client/dist/requests/documents/RetrieveDocumentZip';
import { ListCultures } from '@kurtosys/ksys-api-client/dist/requests/config/ListCultures';
import { ListProperties } from '@kurtosys/ksys-api-client/dist/requests/config/ListProperties';
import { GetFilters as DocumentGetFilters } from '@kurtosys/ksys-api-client/dist/requests/documents/GetFilters';
import { GetFilters as FundGetFilters } from '@kurtosys/ksys-api-client/dist/requests/fund/GetFilters';

export class Services {
    apiUrl;
    headers;
    @observable token: string = '';

    constructor(apiUrl, token) {
        this.apiUrl = apiUrl;
        this.token = token;
        this.headers = {
            'X-KSYS-TOKEN': token,
            'X-KSYS-EXPERIMENTAL-TRANSLATION': true,
        };
    }

    @computed
    get wrappedToken(): { value: string } {
        return { value: this.token };
    }

    @computed
    get serviceUrl() {
        return `${this.apiUrl}/services/`;
    }

    getData = async (
        fundList,
        fundListExclusion,
        filterSearch,
        identifierSearch,
        sort,
        advancedSort,
        documentsJoinSpec,
        start,
        limit,
        cultureCode,
        sourceCulture,
        cultureSearchTransform: CultureSearchTransform,
        timeseriesInclude,
        statisticsInclude,
        allocationsInclude,
        clientCode = [''],
        include = undefined,
        translate = true,
        applyFormats = true,
        disclaimers = true,
        includeProperties = undefined
    ) => {
        cultureCode = cultureSearchTransformReplace(
            cultureSearchTransform,
            cultureCode
        );
        const body = {
            type: 'CLSS',
            clientCode,
            fundList,
            fundListExclusion,
            search: filterSearch,
            identifierSearch,
            sort,
            advancedSort,
            include: include || {
                timeseries: timeseriesInclude,
                statistics: statisticsInclude,
                allocations: allocationsInclude,
                disclaimers: {},
                commentaries: {},
                fundManagers: {},
            },
            limit,
            start,
            culture: cultureCode,
            sourceCulture,
            translate,
            applyFormats,
            disclaimers,
            includeProperties,
        } as any;

        if (documentsJoinSpec) {
            body.include.documents = {
                joinSpec: documentsJoinSpec,
            };
        }

        return this.getSearchEntity(body);
    };

    getDataSimplified = async (
        fundList,
        fundListExclusion,
        filterSearch,
        identifierSearch,
        include = undefined,
        includeProperties = undefined
    ) => {
        const body = {
            type: 'CLSS',
            fundList,
            fundListExclusion,
            search: filterSearch,
            identifierSearch,
            include: include || {},
            includeProperties,
        } as any;

        return this.getSearchEntity(body);
    };

    getDocuments = async (search = {}) => {
        const body = {
            type: 'CLSS',
            ...search,
        };

        return this.getSearchDocuments(body);
    };

    getFilters = async (
        filterProperties = [],
        search = [],
        fundList: string | Array<string> = '',
        fundListExclusion = []
    ) => {
        const body = {
            type: 'CLSS',
            filterProperties,
            search,
            fundList: isArrayLike(fundList) ? fundList : [fundList],
            fundListExclusion,
        };

        return this.getFundGetFilters(body) as any;
    };

    getDocumentFilters = async (codes = [], search = []) => {
        const body = {
            type: 'CLSS',
            codes,
            search,
        };

        return this.getDocumentGetFilters(body) as any;
    };

    getTranslation = async culture => {
        const body = {
            culture,
        };

        return this.getListTranslations(body);
    };

    getProperties = async entityType => {
        const body = {
            entityType,
        };

        return this.getListProperties(body) as any;
    };

    documentDownload = async clientCode => {
        const body = {
            clientCode,
        };

        return this.getRetrieveDocument(body) as any;
    };

    documentDownloadZip = async documentCodes => {
        const body = {
            documentCodes,
        };

        return this.getRetrieveDocumentZip(body);
    };

    getCultures = async () => {
        const body = {};

        return this.getListCultures(body) as any;
    };

    getDisclaimers = async () => {
        // only fetch unlinked disclaimers
        const body = { global: true };

        return this.getListDisclaimers(body);
    };

    getFundList = async (listName: string | Array<string>) => {
        const body: any = { useEntityAlias: false };

        // property naming differences between the listName array vs listname string
        if (isArrayLike(listName)) {
            body.listName = listName;
        } else {
            body.listname = listName;
        }

        return this.getListFundLists(body);
    };

    // api client calls
    getSearchEntity = async body => {
        const searchEntity = new SearchEntity(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return searchEntity.execute();
    };

    getSearchDocuments = async body => {
        const searchDocuments = new SearchDocuments(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return searchDocuments.execute();
    };

    getListDisclaimers = async body => {
        const listDisclaimers = new ListDisclaimers(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return listDisclaimers.execute();
    };

    getListFundLists = async body => {
        const listFundLists = new ListFundLists(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return listFundLists.execute();
    };

    getListTranslations = async body => {
        const listTranslations = new ListTranslations(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return listTranslations.execute();
    };

    getRetrieveDocument = async body => {
        const retrieveDocument = new RetrieveDocument(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return retrieveDocument.execute();
    };

    getRetrieveDocumentZip = async body => {
        const retrieveDocument = new RetrieveDocumentZip(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return retrieveDocument.execute();
    };

    getDocumentGetFilters = async body => {
        const documentGetFilters = new DocumentGetFilters(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return documentGetFilters.execute();
    };

    getFundGetFilters = async body => {
        const fundGetFilters = new FundGetFilters(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return fundGetFilters.execute();
    };

    getListCultures = async body => {
        const listCultures = new ListCultures(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return listCultures.execute();
    };

    getListProperties = async body => {
        const listProperties = new ListProperties(this.wrappedToken, {
            body,
            baseUrl: this.serviceUrl,
        });
        return listProperties.execute();
    };
}



// WEBPACK FOOTER //
// ./src/services/services.ts